import React from 'react';

import { useQueryShopItems } from '@hooks/api/shop';

import {
	SORT_ORDER,
	type ShopItemsQueryParams,
	type ShopFiltersState,
	type ShopItem as ShopItemType,
	type Category
} from '@types';

const PER_PAGE = 8;

export const useShopData = () => {
	const [filtersState, setFiltersState] = React.useState<ShopFiltersState>({
		selectedCategories: [],
		sortOrder: SORT_ORDER.ASC,
		currentPage: 1
	});

	// TODO(gabdykal): refactor this state management
	const [accumulatedItems, setAccumulatedItems] = React.useState<ShopItemType[]>([]);
	const [isLoadingMore, setIsLoadingMore] = React.useState(false);
	const [lastLoadedPage, setLastLoadedPage] = React.useState(0);
	const [isInitialLoad, setIsInitialLoad] = React.useState(true);
	const [cachedCategories, setCachedCategories] = React.useState<Category[]>([]);
	const [categoryTags, setCategoryTags] = React.useState<Record<string, string>>({});
	const [isFilterLoading, setIsFilterLoading] = React.useState(false);
	const [isPagination, setIsPagination] = React.useState(false);

	const queryParams: ShopItemsQueryParams = React.useMemo(
		() => ({
			category_ids: filtersState.selectedCategories.length > 0 ? filtersState.selectedCategories : undefined,
			order: filtersState.sortOrder,
			page: filtersState.currentPage,
			per_page: PER_PAGE
		}),
		[filtersState]
	);

	const { data: shopData, isLoading, error } = useQueryShopItems(queryParams);

	const handleFiltersChange = React.useCallback((newFilters: ShopFiltersState) => {
		setIsFilterLoading(true);
		setFiltersState(newFilters);
		setAccumulatedItems([]);
		setLastLoadedPage(0);
		setIsInitialLoad(false);
	}, []);

	const handleLoadMore = React.useCallback(async () => {
		if (!shopData?.pagination?.next_page) return;

		setIsLoadingMore(true);
		setFiltersState(prev => ({
			...prev,
			currentPage: shopData?.pagination?.next_page || 1
		}));
	}, [shopData?.pagination?.next_page]);

	const handlePagination = React.useCallback(async (page: number) => {
		setIsLoadingMore(true);
		setIsPagination(true);

		setFiltersState(prev => ({
			...prev,
			currentPage: page
		}));
	}, []);

	React.useEffect(() => {
		if (shopData) {
			if (shopData.meta?.categories && shopData.meta.categories.length > 0) {
				setCachedCategories(prev => {
					if (JSON.stringify(prev) !== JSON.stringify(shopData.meta.categories)) {
						return shopData.meta.categories;
					}
					return prev;
				});
			}

			if (shopData.meta?.category_tags) {
				setCategoryTags(prev => {
					if (JSON.stringify(prev) !== JSON.stringify(shopData.meta.category_tags)) {
						return shopData.meta.category_tags || {};
					}
					return prev;
				});
			}

			if (shopData.data && shopData.data.length > 0 && !isLoading) {
				const currentPage = queryParams.page || 1;

				if (isPagination) {
					setAccumulatedItems(shopData.data);
					setIsFilterLoading(false);
				} else {
					if (currentPage === 1) {
						setAccumulatedItems(shopData.data);
						setLastLoadedPage(1);
						setIsInitialLoad(false);
						setIsFilterLoading(false);
					} else if (currentPage > lastLoadedPage) {
						setAccumulatedItems(prev => {
							const newItems = shopData.data.filter(
								newItem => !prev.some(existingItem => existingItem.id === newItem.id)
							);
							return newItems.length > 0 ? [...prev, ...newItems] : prev;
						});
						setLastLoadedPage(currentPage);
					}
				}

				setIsLoadingMore(false);
			} else if (!isLoading && shopData.data && shopData.data.length === 0) {
				setIsFilterLoading(false);
				setIsInitialLoad(false);
				setAccumulatedItems([]);
			}
		} else if (shopData && !isLoading) {
			setIsFilterLoading(false);
			setIsInitialLoad(false);
			setAccumulatedItems([]);
		}

		// Handle API error - stop loading states
		if (error && !isLoading) {
			setIsFilterLoading(false);
			setIsInitialLoad(false);
			setAccumulatedItems([]);
		}
	}, [shopData, queryParams.page, lastLoadedPage, isLoading, error]);

	const hasItems = accumulatedItems.length > 0;
	const hasMore = Boolean(shopData?.pagination?.next_page);
	const totalItems = shopData?.pagination?.total_count || 0;
	const displayedItems = accumulatedItems.length;

	const combinedIsLoading = isLoading || isFilterLoading;

	return {
		// State
		filtersState,
		accumulatedItems,
		isLoadingMore,
		isInitialLoad,
		cachedCategories,
		categoryTags,

		// Data
		shopData,
		isLoading: combinedIsLoading,
		hasItems,
		hasMore,
		totalItems,
		displayedItems,

		// Handlers
		handleFiltersChange,
		handleLoadMore,
		handlePagination
	};
};
